import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Card, Button, Box, IconButton, Chip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  Edit,
} from "@mui/icons-material";
import { GetLinksByCountry } from "../../redux/actions/GetLinksByCountryAction";
import { GetLevels1 } from "../../redux/actions/GetLevels1Action";
import { GetLevels2 } from "../../redux/actions/GetLevels2Action";
import AddLinkModal from "components/AddLinkModal";
import EditLinkModal from "components/EditLinkModal";

const LinksTable = () => {
  const dispatch = useDispatch();
  const linksResponse = useSelector((state) => state.links.links);
  const levels1Response = useSelector((state) => state.levels1.levels1);
  const levels2Response = useSelector((state) => state.levels2.levels2);
  const [levels1, setLevels1] = useState([]);
  const [levels2, setLevels2] = useState([]);
  const [links, setLinks] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (linksResponse) {
      const listLinks = linksResponse.map((element) => ({
        id: element.idMenuLevel3Links ?? element.idmenuLevel3Links,
        linkName: element.linkName ?? element.name,
        url: element.url ?? element.URL,
        isActive: element.isActive ?? true,
        level1: element.menuLevel1,
        level1Id: element.idMenuLevel1 ?? element.idmenuLevel1,
        level2: element.menuLevel2,
        level2Id: element.idMenuLevel2 ?? element.idmenuLevel2,
        createdBy: element.createdBy,
        createdDate: element.createdDate,
        modifiedBy: element.modifiedBy,
        modifiedDate: element.modifiedDate,
        idCountry: element.idcountry ?? element.idCountry,
      }));

      setLinks(listLinks);
    }
  }, [linksResponse]);

  useEffect(() => {
    if (levels1Response && levels1Response.length > 0) {
      setLevels1(levels1Response);
    }
  }, [levels1Response]);

  useEffect(() => {
    if (Array.isArray(levels2Response) && levels2Response.length > 0) {
      setLevels2(levels2Response);
    }
  }, [levels2Response]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await dispatch(GetLinksByCountry());
        await dispatch(GetLevels1());
        await dispatch(GetLevels2());
      } catch (error) {
        console.error("Error loading links:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handlers
  const handleAddOpen = () => {
    setOpenAddModal(true);
  };

  const handleEditOpen = (linkData) => {
    setSelectedLink(linkData);
    setOpenEditModal(true);
  };

  const formatDate = (params) => {
    if (!params.value) return "";

    const date = new Date(params.value);

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${month}-${day}-${year} ${hours}:${minutes}`;
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 60,
    },
    {
      field: "linkName",
      headerName: "Link Name",
      width: 250,
    },
    {
      field: "url",
      headerName: "URL",
      width: 350,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </span>
          <IconButton
            size="small"
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: "level1",
      headerName: "Type",
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="primary" />
      ),
    },
    {
      field: "level2",
      headerName: "Category",
      width: 140,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: "isActive",
      headerName: "Active",
      width: 70,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Yes" : "No"}
          size="small"
          color={params.value ? "success" : "default"}
        />
      ),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 120,
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 200,
      renderCell: (params) => {
        return formatDate(params);
      },
    },
    {
      field: "modifiedBy",
      headerName: "Modified By",
      width: 120,
    },
    {
      field: "modifiedDate",
      headerName: "Modified Date",
      width: 200,
      renderCell: (params) => {
        return formatDate(params);
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Button
          variant="contained"
          sx={{
            color: "#ffffff",
            backgroundColor: "#4b4bfa",
            "&:hover": {
              backgroundColor: "#3e3ed9",
            },
          }}
          onClick={() => handleEditOpen(params.row)}
        >
          <Edit />
        </Button>
      ),
    },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <div style={{ height: "100%", width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <h1 style={{ marginBottom: "0.5%" }}>Links Management</h1>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#4b4bfa",
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#3e3ed9",
                  },
                }}
                onClick={handleAddOpen}
              >
                <AddIcon />
              </Button>
            </Box>
            <DataGrid
              rows={links}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 20]}
              disableSelectionOnClick
              loading={loading}
              sx={{
                "& .MuiCircularProgress-root": {
                  color: "#4b4bfa",
                },
                mt: 2,
              }}
            />
          </div>
        </Card>
      </Grid>

      <AddLinkModal
        levels1={levels1}
        levels2={levels2}
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
      />
      <EditLinkModal
        levels1={levels1}
        levels2={levels2}
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setSelectedLink(null);
        }}
        linkData={selectedLink}
      />
    </Grid>
  );
};

export default LinksTable;
